import React, { useState, useEffect, useCallback } from "react";
import { Copy, Check, Trash2, KeyRound, AlertTriangle, Download } from "lucide-react";
import apiService from "../services/apiService";

// The download endpoint is deliberately unauthenticated (see
// app/routes/api_tokens.py's download_extension) — the extension's own
// content isn't sensitive, so a plain link works. No fetch-as-blob needed.
const EXTENSION_DOWNLOAD_URL = `${apiService.getApiUrl()}/extension/download`;

function ExtensionAccess() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [justCreated, setJustCreated] = useState(null); // { token, label }
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.listApiTokens();
      if (res?.status !== "success") throw new Error(res?.message || "Failed to load tokens");
      setTokens(res.tokens || []);
    } catch (err) {
      setError(err?.message || "Failed to load tokens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiService.createApiToken(newLabel.trim() || undefined);
      if (res?.status !== "success") throw new Error(res?.message || "Failed to create token");
      setJustCreated({ token: res.token, label: res.label });
      setNewLabel("");
      setCopied(false);
      await fetchTokens();
    } catch (err) {
      setError(err?.message || "Failed to create token");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!justCreated) return;
    try {
      await navigator.clipboard.writeText(justCreated.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — the token is still selectable text.
    }
  };

  const handleRevoke = async (tokenId) => {
    if (!window.confirm("Revoke this token? The browser extension using it will stop working immediately.")) return;
    setRevokingId(tokenId);
    setError(null);
    try {
      const res = await apiService.revokeApiToken(tokenId);
      if (res?.status !== "success") throw new Error(res?.message || "Failed to revoke token");
      await fetchTokens();
    } catch (err) {
      setError(err?.message || "Failed to revoke token");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "#ef5366" }}
        >
          <KeyRound size={20} className="text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Extension Access</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Generate an API token here and paste it into the HubSpot/Trellus browser
        extension's options page. The extension uses it to push the current call's
        company name into your active cold-call session, and to clear the session
        automatically between calls — without you touching SalesBot.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-1">Step 1 — Download the extension</h2>
        <p className="text-sm text-gray-600 mb-3">
          Unzip it, then in Chrome go to <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">chrome://extensions</code>,
          enable <strong>Developer mode</strong>, and click <strong>Load unpacked</strong> on the unzipped folder.
        </p>
        <a
          href={EXTENSION_DOWNLOAD_URL}
          download="salesbot-extension.zip"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ backgroundColor: "#ef5366" }}
        >
          <Download size={16} />
          Download Extension
        </a>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Step 2 — Generate a new token</h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder='Label (e.g. "Work laptop")'
            disabled={creating}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#ef5366" }}
          >
            {creating ? "Generating…" : "Generate Token"}
          </button>
        </div>

        {justCreated && (
          <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800 font-medium mb-2">
              Copy this token now — it won't be shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded text-xs break-all select-all">
                {justCreated.token}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-2 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-1"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Active tokens</h2>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-gray-500">No tokens yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {tokens.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    {t.label || "Untitled"}
                    {t.revoked_at && (
                      <span className="ml-2 text-xs font-normal text-red-600">Revoked</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Created {new Date(t.created_at).toLocaleString()}
                    {t.last_used_at && ` · Last used ${new Date(t.last_used_at).toLocaleString()}`}
                  </div>
                </div>
                {!t.revoked_at && (
                  <button
                    type="button"
                    onClick={() => handleRevoke(t.id)}
                    disabled={revokingId === t.id}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                    {revokingId === t.id ? "Revoking…" : "Revoke"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExtensionAccess;
